import { Environment as ENV } from '@celito.clients/singleton';
import { history } from '@celito.clients/utils';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { isString } from 'lodash';

import { LogLevel, TAttributes, TMessage, Transport, User } from '../types';

export const appInsightsReactPlugin = new ReactPlugin();

export class AppInsightsTransport implements Transport {
  appInsights?: ApplicationInsights;

  constructor() {
    const connectionString = String(
      ENV.getInstance().getParam('APPINSIGHTS_CONNECTION_STRING') ?? ''
    ).trim();

    // App Insights is optional. When no connection string is configured
    // (e.g. an isolated deployment with App Insights disabled), skip
    // initialization entirely: the App Insights SDK throws on an empty/invalid
    // connection string, and because the logger is built during app bootstrap
    // inside the root error boundary, that throw would crash the whole app.
    if (
      !connectionString ||
      connectionString === 'undefined' ||
      connectionString === 'null'
    ) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        instrumentationKey: String(
          ENV.getInstance().getParam('APPINSIGHTS_INSTRUMENTATIONKEY')
        ),

        extensions: [appInsightsReactPlugin],
        extensionConfig: {
          [appInsightsReactPlugin.identifier]: { history },
        },
        enableAutoRouteTracking: true, // temporary to analyze the metrics
      },
    });

    this.appInsights.loadAppInsights();

    this.appInsights.context.application.build = String(
      ENV.getInstance().getParam('SENTRY_RELEASE_NAME')
    );
  }

  handle(message: TMessage, level: LogLevel, attributes?: TAttributes): void {
    const appInsights = this.appInsights;
    if (!appInsights) return;

    if ([LogLevel.DEBUG, LogLevel.INFO].includes(level)) {
      if (!isString(message)) return;

      appInsights.trackTrace({
        message,
        properties: {
          attributes,
          level,
        },
      });
    } else {
      message = isString(message) ? new Error(message) : message;
      appInsights.trackException({
        exception: message,
        properties: {
          attributes,
          level,
        },
      });
      appInsights.flush();
    }
  }

  identity(user: User) {
    const appInsights = this.appInsights;
    if (!appInsights) return;

    appInsights.setAuthenticatedUserContext(
      user?.name ?? '',
      user?.email,
      true
    );
  }
}
